import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, V2Badge, V2Button, V2Card, V2CardBody, V2Tag } from '@sakhtar/design-system/react';
import whiteLogoUrl from '@sakhtar/design-system/assets/logo/sakhtar-white-logo.svg';
import { getDemoStatus } from '../../lib/publicApi.js';
import { loginDemo } from '../../lib/auth.js';
import { DemoDashboardIllustration } from './DemoDashboardIllustration.js';
import { BuildWorkspaceIllustration } from './BuildWorkspaceIllustration.js';
import { START_DEMO_LABEL, START_LOGIN_LABEL, START_TRIAL_LABEL } from './copy.js';
import { HeroSquaresCanvas, HeroGlowBlobs } from '../auth/AuthHeroPanel.js';
import './start-page.css';

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Onboarding Desktop.html §A1 "Intro" — 3-benefit grid,
 *   single "شروع ثبت‌نام" CTA, no pricing/module picker. Already compared
 *   screen-by-screen in `docs/agents/handoffs/onboarding-design-review.md`
 *   Part 1, row A1: the real page's 2-card layout (demo + trial) is an
 *   **intentional** divergence per ERP-218's own scope note (the
 *   Instagram entry point needs both a demo and a trial CTA), not a gap.
 * status: PARTIAL_REAL (pre-existing, per that handoff).
 *
 * Resolved divergence (PR #173): The third card ("ورود به فضای کاری موجود"
 * → `/start/login`) was reconciled to provide an existing-tenant entry point
 * alongside Demo and Trial, replacing the experimental direct-checkout link
 * (`CheckoutPage.tsx`'s own route, still reachable directly, just no longer
 * linked from this landing page — see `start-slug-login.test.ts`, which
 * asserts this landing page's own source no longer names that route).
 *
 * Visual refresh: restyled against a Claude Design canvas mockup the user
 * reviewed and approved in the same session ("Sakhtar — صفحه شروع (Start)",
 * built from the same Sakhtar Design System `--sk-*` tokens this file
 * already used via V2* primitives). The existing-tenant login entry point
 * moved from a third stacked card into the topbar pill (`START_LOGIN_LABEL`,
 * still `/start/login`) — a layout change only, all three destinations
 * (`openDemo`, `/start/trial`, `/start/login`) are unchanged.
 */
export function StartPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function openDemo() {
    setBusy(true);
    setError(null);
    try {
      const demo = await getDemoStatus();
      if (!demo.available || (demo.slug !== 'demo' && demo.slug !== 'demo-preview')) {
        setError('محیط دمو هنوز آماده نشده است. پس از اجرای seed دمو دوباره تلاش کنید.');
        return;
      }
      try {
        const result = await loginDemo(demo.slug);
        navigate(result.redirectTo || `/t/${demo.slug}/admin`);
      } catch {
        navigate(`/t/${demo.slug}/login`);
      }
    } catch {
      setError('ارتباط با سرور برقرار نشد.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="sk-app sk-safe-top"
      dir="rtl"
      style={{
        width: '100%',
        minHeight: '100dvh',
        position: 'relative',
        overflow: 'hidden',
        background: 'var(--sk-brand-800, #23277a)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Same animated square-grid + ambient glow as the login screens'
          hero panel (AuthHeroPanel.tsx) — reported live as wanted here
          too, across the whole page rather than just a side panel. Both
          are position:absolute with z-index:auto, so per CSS2.1's
          painting order they'd normally paint ABOVE plain in-flow
          content; wrapping everything below in its own position:relative
          div (same pattern AuthHeroPanel.tsx itself uses for its content)
          keeps the real page on top instead. */}
      <HeroSquaresCanvas />
      <HeroGlowBlobs />

      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* Topbar — brand at the inline-start edge (right, in RTL), the
            existing-tenant login pill pushed to the far end via auto margin,
            same pattern as TrialOnboardingPage's topbar. */}
        <div
          className="sk-start-px"
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            rowGap: 12,
            paddingBlock: 20,
            paddingInline: 64,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img
              src={whiteLogoUrl}
              alt="ساختار"
              width={30}
              height={30}
              style={{ display: 'block', flexShrink: 0 }}
            />
            <strong style={{ fontSize: 'var(--sk-fs-lg)', fontWeight: 700, color: '#ffffff' }}>
              ساختار
            </strong>
          </div>
          <V2Button
            type="button"
            variant="secondary"
            icon="chevronLeft"
            onClick={() => navigate('/start/login')}
            style={{ marginInlineStart: 'auto', borderRadius: 'var(--sk-r-pill)' }}
          >
            {START_LOGIN_LABEL}
          </V2Button>
        </div>

        {/* Hero */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 18,
            padding: '8px 24px',
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--sk-fs-5xl)',
              fontWeight: 700,
              lineHeight: 1.25,
              color: '#ffffff',
              maxWidth: 720,
            }}
          >
            چطور می‌خواهید شروع کنید؟
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--sk-fs-lg)',
              color: 'rgba(255,255,255,.72)',
              maxWidth: 560,
              lineHeight: 1.7,
            }}
          >
            اول محصول را با داده‌های نمونه ببینید یا فضای کاری واقعی خودتان را بسازید.
          </p>
        </div>

        {/* Cards */}
        <div
          className="sk-start-px"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 24,
            maxWidth: 1160,
            width: '100%',
            margin: '28px auto 0',
            paddingBlockEnd: 24,
            paddingInline: 64,
          }}
        >
          <V2Card>
            <V2CardBody>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <V2Badge tone="brand" plain>
                  برای آشنایی با محصول
                </V2Badge>
              </div>
              <div style={{ paddingTop: 12 }}>
                <DemoDashboardIllustration />
              </div>
              <h3 style={{ margin: '12px 0 0', fontSize: 'var(--sk-fs-2xl)', fontWeight: 700 }}>
                {START_DEMO_LABEL}
              </h3>
              <p
                style={{
                  margin: '8px 0 0',
                  fontSize: 'var(--sk-fs-md)',
                  color: 'var(--sk-fg-muted)',
                  lineHeight: 1.75,
                }}
              >
                وارد محیط آماده شوید و فروش، مالی، انبار و پروژه‌ها را با داده‌های نمونه فارسی بررسی
                کنید.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                <V2Tag>بدون ورود اطلاعات</V2Tag>
                <V2Tag>داده‌های نمونه</V2Tag>
                <V2Tag>آشنایی سریع</V2Tag>
              </div>
              <V2Button
                type="button"
                variant="primary"
                size="lg"
                icon="chevronLeft"
                onClick={() => void openDemo()}
                disabled={busy}
                style={{ width: '100%', marginTop: 18 }}
              >
                {START_DEMO_LABEL}
              </V2Button>
            </V2CardBody>
          </V2Card>

          <V2Card>
            <V2CardBody>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <V2Badge tone="success" plain>
                  برای شروع کار واقعی
                </V2Badge>
              </div>
              <div style={{ paddingTop: 12 }}>
                <BuildWorkspaceIllustration />
              </div>
              <h3 style={{ margin: '12px 0 0', fontSize: 'var(--sk-fs-2xl)', fontWeight: 700 }}>
                {START_TRIAL_LABEL}
              </h3>
              <p
                style={{
                  margin: '8px 0 0',
                  fontSize: 'var(--sk-fs-md)',
                  color: 'var(--sk-fg-muted)',
                  lineHeight: 1.75,
                }}
              >
                فضای کاری اختصاصی کسب‌وکارتان را با پنل خالی بسازید و دوره آزمایشی ۱۵ روزه را شروع
                کنید.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                <V2Tag>فضای کاری اختصاصی</V2Tag>
                <V2Tag>شروع با داده‌های خودتان</V2Tag>
              </div>
              <V2Button
                type="button"
                variant="primary"
                size="lg"
                icon="chevronLeft"
                onClick={() => navigate('/start/trial')}
                style={{
                  width: '100%',
                  marginTop: 18,
                  background: 'linear-gradient(180deg, var(--sk-brand-800), var(--sk-brand-900))',
                }}
              >
                {START_TRIAL_LABEL}
              </V2Button>
            </V2CardBody>
          </V2Card>
        </div>

        {error ? (
          <div
            className="sk-start-px"
            style={{
              maxWidth: 1160,
              width: '100%',
              margin: '0 auto',
              paddingBlockEnd: 32,
              paddingInline: 64,
            }}
          >
            <Alert tone="warning" title="دمو در دسترس نیست">
              {error}
            </Alert>
          </div>
        ) : null}
      </div>
    </div>
  );
}
